import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Search, Loader2, X, Sparkles, ChevronDown, ChevronUp } from "lucide-react";
import api from "@/lib/api";
import CaseCard from "@/components/CaseCard";
import { ANSOKAN_SECTIONS } from "@/lib/ansokanMall";
import { STATUS_LIST, STATUS, CASE_TYPES } from "@/lib/utils-app";
import { toast } from "sonner";

function NewCaseModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ title: "", address: "", description: "", case_type: "Nybyggnad" });
  const [app, setApp] = useState({});
  const [useMall, setUseMall] = useState(false);
  const [loading, setLoading] = useState(false);
  if (!open) return null;
  const f = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const filled = Object.fromEntries(Object.entries(app).filter(([, v]) => v && v.trim()));
      const payload = useMall && Object.keys(filled).length > 0 ? { ...form, application: filled } : form;
      const { data } = await api.post("/crm/cases", payload);
      toast.success(`Ärende skapat: ${data.ref}`);
      onCreated(data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte skapa ärende");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-900/40 backdrop-blur-sm" data-testid="new-case-modal">
      <div className="w-full max-w-lg bg-white border border-zinc-200 rounded-sm p-6 animate-fade-in-up max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-medium" style={{ fontFamily: "Outfit" }}>Nytt ärende</h3>
          <button onClick={onClose} data-testid="close-new-case" className="p-1 hover:bg-zinc-100 rounded-sm"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Titel</label>
            <input required value={form.title} onChange={f("title")} data-testid="case-title"
              className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 focus:ring-2 focus:ring-blue-100 outline-none text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Fastighetsadress</label>
            <input value={form.address} onChange={f("address")} data-testid="case-address"
              className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 focus:ring-2 focus:ring-blue-100 outline-none text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Ärendetyp</label>
            <select value={form.case_type} onChange={f("case_type")} data-testid="case-type"
              className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm bg-white">
              {CASE_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Beskrivning</label>
            <textarea value={form.description} onChange={f("description")} data-testid="case-description" rows={4}
              className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm" />
          </div>

          <button type="button" onClick={() => setUseMall(!useMall)} data-testid="toggle-ansokan-mall"
            className="w-full flex items-center justify-between px-3 py-2.5 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-sm text-sm font-medium text-blue-800 transition">
            <span className="flex items-center gap-2"><Sparkles className="w-4 h-4" /> Sveriges bästa ansökningsmall (AI-optimerad)</span>
            {useMall ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {useMall && (
            <div className="space-y-3 border border-blue-100 rounded-sm p-3 bg-blue-50/30" data-testid="ansokan-mall-fields">
              <p className="text-xs text-zinc-600 leading-relaxed">
                Fyll i så noggrant som möjligt – ju mer detaljerad information, desto snabbare handläggning och träffsäkrare AI-analys. Hoppa över sektioner som inte är relevanta.
              </p>
              {ANSOKAN_SECTIONS.map((s) => (
                <div key={s.key}>
                  <label className="text-xs font-semibold text-zinc-700">{s.title}</label>
                  <textarea
                    value={app[s.key] || ""}
                    onChange={(e) => setApp({ ...app, [s.key]: e.target.value })}
                    placeholder={s.help}
                    rows={2}
                    data-testid={`ansokan-${s.key}`}
                    className="mt-1 w-full px-3 py-2 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm placeholder:text-zinc-400 placeholder:text-xs"
                  />
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-100 rounded-sm">Avbryt</button>
            <button type="submit" data-testid="submit-new-case" disabled={loading}
              className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white text-sm font-medium rounded-sm flex items-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Skapa"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Cases() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [modal, setModal] = useState(params.get("new") === "1");

  const load = async () => {
    setLoading(true);
    try {
      const opts = {};
      if (status !== "all") opts.status = status;
      if (q) opts.q = q;
      const { data } = await api.get("/crm/cases", { params: opts });
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const onCreated = (c) => setItems([c, ...items]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in-up">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Ärenden</div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-900" style={{ fontFamily: "Outfit" }}>
            Ärendelista
          </h1>
        </div>
        <button data-testid="open-new-case" onClick={() => setModal(true)}
          className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2.5 rounded-sm text-sm font-medium active:scale-[0.98] transition">
          <Plus className="w-4 h-4" /> Nytt ärende
        </button>
      </div>

      {/* Filters */}
      <div className="mt-8 flex flex-col sm:flex-row gap-3 items-stretch">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <input
            data-testid="search-cases"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Sök på titel, adress eller referens..."
            className="w-full pl-10 pr-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 focus:ring-2 focus:ring-blue-100 outline-none text-sm bg-white"
          />
        </div>
        <button onClick={load} data-testid="apply-search"
          className="px-4 py-2.5 border border-zinc-300 rounded-sm text-sm font-medium hover:bg-zinc-100 bg-white">
          Sök
        </button>
      </div>

      <div className="mt-4 flex gap-2 flex-wrap">
        {[{ k: "all", label: "Alla" }, ...STATUS_LIST.map((s) => ({ k: s, label: STATUS[s].label }))].map((f) => (
          <button
            key={f.k}
            data-testid={`filter-${f.k}`}
            onClick={() => setStatus(f.k)}
            className={`px-3 py-1.5 text-xs font-medium uppercase tracking-wider rounded-sm border transition ${
              status === f.k ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-100"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="mt-8">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-zinc-200 rounded-sm py-16 text-center">
            <div className="text-zinc-500 text-sm">Inga ärenden matchar filtren.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {items.map((c) => <CaseCard key={c.case_id} c={c} />)}
          </div>
        )}
      </div>

      <NewCaseModal open={modal} onClose={() => { setModal(false); setParams({}); }} onCreated={onCreated} />
    </div>
  );
}
